import React, { FC, useCallback, useMemo, useState } from 'react';

import BasicButton from 'components/buttons/basicButton';
import DropdownMenu, {
    OptionModel,
} from 'components/nav/DrowpdownMenu/DropdownMenu';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';
import ICONS from '../../constants/icons';

import RelatedMessageRemapModal from 'views/custom-email-templates/RelatedMessageRemapModal';
import AssignTopic from 'views/monitoring/topicManagement/modal/AssignTopic';

interface FormulaBulkActionProps {
    selectedIds: number[];
    selectedRows: any[];
    isDisabled: boolean;
    handleClose: () => void;
}

const FormulaBulkAction: FC<React.PropsWithChildren<FormulaBulkActionProps>> =
    ({ selectedIds, selectedRows, isDisabled, handleClose }) => {
        const [assignTopic, setAssignTopic] = useState<boolean>(false);
        const [remapTemplate, setRemapTemplate] = useState<boolean>(false);

        const handleCancel = useCallback(() => {
            setAssignTopic(false);
            setRemapTemplate(false);
            handleClose();
        }, [handleClose]);

        // Check if all selected messages are email type
        const allEmail = useMemo(() => {
            return selectedRows.every(
                (row) => row.message_type === FormulaType.email,
            );
        }, [selectedRows]);

        // Check if all selected messages have the same email template
        const sameTemplate = useMemo(() => {
            if (!allEmail) return false;
            const firstId =
                selectedRows[0]?.additional_metadata?.email_template_id;
            if (!firstId) return false;
            return !selectedRows.some(
                (row) =>
                    !row.additional_metadata?.email_template_id ||
                    row.additional_metadata?.email_template_id !== firstId,
            );
        }, [selectedRows, allEmail]);

        const actionOptions = useMemo<OptionModel[]>(() => {
            const options: OptionModel[] = [
                {
                    name: 'Assign to topic',
                    action: () => setAssignTopic(true),
                },
            ];
            if (allEmail) {
                options.push({
                    name: 'Remap email template',
                    action: () => setRemapTemplate(true),
                    isDisabled: !sameTemplate,
                    tooltipText: !sameTemplate
                        ? 'All selected messages must use the same email template'
                        : undefined,
                });
            }
            return options;
        }, [allEmail, sameTemplate]);

        return (
            <Container>
                <BasicButton
                    name="Cancel"
                    action={handleCancel}
                    isPrimary={false}
                />
                <DropdownMenu
                    options={actionOptions}
                    buttonConfig={{
                        buttonText: 'Action',
                        isPrimary: true,
                        isTrailingIcon: true,
                        menuOpenIcon: (
                            <ICONS.chevronUp
                                style={{ width: 16, height: 16 }}
                            />
                        ),
                        menuCloseIcon: (
                            <ICONS.chevronDown
                                style={{ width: 16, height: 16 }}
                            />
                        ),
                    }}
                />
                {assignTopic && (
                    <AssignTopic
                        handleClose={handleClose}
                        formulaIds={selectedIds}
                        isDisabled={isDisabled}
                    />
                )}
                {remapTemplate && (
                    <RelatedMessageRemapModal
                        selectedMessages={selectedRows}
                        handleClose={handleClose}
                    />
                )}
            </Container>
        );
    };

const Container = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(2),
    alignItems: 'center',
});

export default FormulaBulkAction;
